import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API_URL, formatDate, getStatusBadgeClass } from '../lib/utils';
import { toast } from 'sonner';
import {
  Briefcase,
  Users,
  FileText,
  Loader2,
  Building2,
  UserCheck,
  TrendingUp,
  DollarSign,
  Settings,
  CreditCard,
  Shield,
  Upload,
  Rss,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Mail,
  MessageSquare,
  MousePointerClick,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Send,
  Clock,
  BadgeCheck,
  ExternalLink,
  XCircle,
  MapPin,
  Bell,
  Eye,
} from 'lucide-react';
import { useAdminCtx } from './AdminContext';


const AdminTabSettings = () => {
  const { handleSaveSettings, ownerSettings, savingSettings, setOwnerSettings } = useAdminCtx();
  return (
    <>
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <Settings className="w-6 h-6 text-primary" />
                <h3 className="font-semibold text-gray-900">Owner Settings</h3>
              </div>
              
              <div className="space-y-6 max-w-2xl">
                <div>
                  <Label htmlFor="business_name">Business Name</Label>
                  <Input
                    id="business_name"
                    value={ownerSettings?.business_name || ''}
                    onChange={(e) => setOwnerSettings({ ...ownerSettings, business_name: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="business_email">Business Email</Label>
                  <Input
                    id="business_email"
                    type="email"
                    value={ownerSettings?.business_email || ''}
                    onChange={(e) => setOwnerSettings({ ...ownerSettings, business_email: e.target.value })}
                    placeholder="support@dsws.com"
                    className="mt-1"
                  />
                </div>

                <div className="border-t pt-6">
                  <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Payment Settings
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="stripe_account_id">Stripe Account ID (for payouts)</Label>
                      <Input
                        id="stripe_account_id"
                        value={ownerSettings?.stripe_account_id || ''}
                        onChange={(e) => setOwnerSettings({ ...ownerSettings, stripe_account_id: e.target.value })}
                        placeholder="acct_xxxxxxxxxxxxx"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Get this from your Stripe Dashboard → Settings → Account Details
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="paypal_email">PayPal Email (for payouts)</Label>
                      <Input
                        id="paypal_email"
                        type="email"
                        value={ownerSettings?.paypal_email || ''}
                        onChange={(e) => setOwnerSettings({ ...ownerSettings, paypal_email: e.target.value })}
                        placeholder="your-paypal@email.com"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Policies
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="refund_policy">Refund Policy</Label>
                      <Textarea
                        id="refund_policy"
                        value={ownerSettings?.refund_policy || ''}
                        onChange={(e) => setOwnerSettings({ ...ownerSettings, refund_policy: e.target.value })}
                        rows={5}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="privacy_policy_summary">Privacy Policy Summary</Label>
                      <Textarea
                        id="privacy_policy_summary"
                        value={ownerSettings?.privacy_policy_summary || ''}
                        onChange={(e) => setOwnerSettings({ ...ownerSettings, privacy_policy_summary: e.target.value })}
                        rows={5}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="bg-primary hover:bg-primary-hover text-white"
                  data-testid="save-settings-btn"
                >
                  {savingSettings ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Settings'
                  )}
                </Button>
              </div>
            </div>
    </>
  );
};

export default AdminTabSettings;
